import { Plan, PlanName } from '@tribeplatform/gql-client/types'
import type { I18nShape } from '@tribeplatform/react-components/i18n'

export const getSeatsCapacity = (
  networkSeatsCapacity: number,
  planName: PlanName,
): number => {
  return Math.max(planSeatsCapacity(planName), networkSeatsCapacity, 1)
}

export const hasPlan = (plan: Plan) => {
  return (
    plan &&
    plan?.name !== PlanName.basic &&
    plan?.name !== PlanName.Basic &&
    !plan?.trial
  )
}

export const planDescriptor = (intl: I18nShape) => (planName: PlanName) => {
  const { $t } = intl
  switch (planName) {
    case PlanName.Enterprise:
      return $t({
        defaultMessage:
          'For large businesses or those in regulated industries.',
        id: 'Admin.Plans.Enterprise.Description',
      })
    case PlanName.Advanced:
      return $t({
        defaultMessage: 'Advanced tools for medium and large businesses.',
        id: 'Admin.Plans.Advanced.Description',
      })
    default:
      return ''
  }
}

export const planFeatures = (
  intl: I18nShape,
  planName?: PlanName,
): string[] => {
  const { $t } = intl
  switch (planName) {
    case PlanName.Enterprise:
      return [
        $t({
          defaultMessage: 'Greatness of Advanced, and:',
          id: 'Plans.Features.OnTopOfAdvanced',
        }),
        $t({
          defaultMessage: 'Enterprise-grade security',
          id: 'Plans.Features.EnterpriseSecurity',
        }),
        $t({
          defaultMessage: 'Data residency',
          id: 'Plans.Features.DataResidency',
        }),
        $t({
          defaultMessage: 'Audit and activity log',
          id: 'Plans.Features.AuditLog',
        }),
        $t({
          defaultMessage: 'Uptime SLA',
          id: 'Plans.Features.SLA',
        }),
        $t({
          defaultMessage: 'Customer success manager',
          id: 'Plans.Features.CSM',
        }),
        $t({
          defaultMessage: 'Custom billing',
          id: 'Plans.Features.CustomBilling',
        }),
      ]
    case PlanName.Advanced:
      return [
        $t({
          defaultMessage: 'Advanced apps',
          id: 'Plans.Features.AdvancedApps',
        }),
        $t({
          defaultMessage: 'Single Sign-On (SSO)',
          id: 'Plans.Features.Sso',
        }),
        $t({
          defaultMessage: 'Fully white-label',
          id: 'Plans.Features.WhiteLabel',
        }),
        $t({
          defaultMessage: 'API and webhooks',
          id: 'Plans.Features.API',
        }),
        $t({
          defaultMessage: 'Staging environment',
          id: 'Plans.Features.Staging',
        }),
        $t({
          defaultMessage: 'Customer success manager',
          id: 'Plans.Features.CSM',
        }),
      ]
    default:
      return []
  }
}

export const plans = [PlanName.Advanced, PlanName.Enterprise]

export const planMemberCapacity = (planName?: PlanName): number => {
  switch (planName) {
    case PlanName.Enterprise:
    case PlanName.enterprise:
    case PlanName.Advanced:
      return 100000
    default:
      return 3
  }
}

export const planSeatsCapacity = (planName?: PlanName): number => {
  switch (planName) {
    case PlanName.Enterprise:
    case PlanName.enterprise:
      return 10
    case PlanName.Advanced:
      return 10
    case PlanName.Premium:
    case PlanName.premium:
      return 5
    case PlanName.Plus:
    case PlanName.plus:
      return 3
    case PlanName.Basic:
    case PlanName.basic:
      return 3
    default:
      return 3
  }
}

const PlanOrder = {
  [PlanName.basic]: 0,
  [PlanName.Basic]: 0,
  [PlanName.plus]: 1,
  [PlanName.Plus]: 1,
  [PlanName.premium]: 2,
  [PlanName.Premium]: 2,
  [PlanName.Advanced]: 3,
  [PlanName.enterprise]: 4,
  [PlanName.Enterprise]: 4,
}

export const isPlanDowngrade = (
  targetPlan: PlanName,
  currentPlan: PlanName,
): boolean => {
  if (!currentPlan || !targetPlan) {
    return false
  }
  return PlanOrder[currentPlan] > PlanOrder[targetPlan]
}

export const isHigherPlan = (currentPlan: PlanName, targetPlan: PlanName) => {
  if (!currentPlan || !targetPlan) {
    return false
  }
  return PlanOrder[currentPlan] >= PlanOrder[targetPlan]
}

export const PlanYearlyPercentOff = {
  [PlanName.plus]: 40,
  [PlanName.Plus]: 40,
  [PlanName.premium]: 25,
  [PlanName.Premium]: 25,
  [PlanName.Advanced]: 14,
}

export const getTranslatedPlanName = (
  { $t }: I18nShape,
  planName?: PlanName,
  respectLegacyPlans = false,
): string => {
  switch (planName) {
    case PlanName.Enterprise:
    case PlanName.enterprise:
      return $t({ defaultMessage: 'Enterprise', id: 'Plans.Enterprise.Name' })
    case PlanName.Advanced:
      return $t({ defaultMessage: 'Advanced', id: 'Plans.Advanced.Name' })
    case PlanName.Premium:
    case PlanName.premium:
    case PlanName.PremiumLegacy:
      if (respectLegacyPlans) {
        return $t({ defaultMessage: 'Premium', id: 'Plans.Premium.Name' })
      }
      return $t({ defaultMessage: 'Advanced', id: 'Plans.Advanced.Name' })
    case PlanName.Plus:
    case PlanName.plus:
    case PlanName.PlusLegacy:
      if (respectLegacyPlans) {
        return $t({ defaultMessage: 'Plus', id: 'Plans.Plus.Name' })
      }
      return $t({ defaultMessage: 'Advanced', id: 'Plans.Advanced.Name' })
    case PlanName.basic:
    case PlanName.Basic:
      return $t({ defaultMessage: 'Free', id: 'Plans.Free.Name' })
    default:
      return $t({ defaultMessage: 'Free', id: 'Plans.Free.Name' })
  }
}
